import { FormEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getUserId } from '@paradigm/store/src/auth/selectors';

import {
  clearStoredAgentSession,
  deriveDefaultAgentUrl,
  getStoredAgentUrl,
  isValidAgentUrl,
  setStoredAgentUrl,
} from '#/features/terminal/agent-url';

/**
 * Settings sub-view for the OpenClaw gateway WebSocket URL used by the
 * block-trade AI terminal. It reads and writes the terminal's own (per-user,
 * per-device) storage — the same key the terminal reads on connect — so a URL
 * set here prefills the terminal's login screen.
 *
 * The pairing token is entered inside the terminal itself (that flow needs the
 * live WebSocket), so this view owns only the URL.
 */
export default function SettingsBlockTerminal() {
  const userId = useSelector(getUserId);
  const user = useSelector((state) => state.auth.user);
  const deskTicker = user?.trading_desk?.ticker;
  const userIdStr = userId != null ? String(userId) : null;

  const defaultUrl =
    deskTicker != null && userId != null
      ? deriveDefaultAgentUrl(deskTicker, userId)
      : '';
  const storedUrl = userIdStr != null ? getStoredAgentUrl(userIdStr) : null;
  const [draft, setDraft] = useState(storedUrl ?? defaultUrl);
  // localStorage isn't reactive, so track the last-saved value in state to
  // drive the Save-enabled state and the "Saved." confirmation.
  const [savedUrl, setSavedUrl] = useState<string | null>(storedUrl);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const trimmed = draft.trim();
  const isValid = trimmed.length === 0 || isValidAgentUrl(trimmed);
  const hasChanges = trimmed !== (savedUrl ?? '');

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    if (userIdStr == null) return;

    if (trimmed.length === 0) {
      // Clearing the URL — also drop tokens for the prior pairing so the
      // next setup starts from a clean slate.
      clearStoredAgentSession(userIdStr);
      setStoredAgentUrl(userIdStr, '');
      setSavedUrl(null);
    } else {
      // If switching to a different gateway, drop the prior session tokens
      // so the next connect re-pairs against the new instance instead of
      // replaying a stale device token.
      if (savedUrl != null && savedUrl !== trimmed) {
        clearStoredAgentSession(userIdStr);
      }
      setStoredAgentUrl(userIdStr, trimmed);
      setSavedUrl(trimmed);
    }
    setSavedAt(Date.now());
  };

  if (userId == null) {
    return <Hint>Sign in to configure the DIME terminal.</Hint>;
  }

  return (
    <Form onSubmit={handleSave}>
      <Description>
        WebSocket URL of your OpenClaw gateway. The DIME terminal pairs this
        user with that gateway and pushes the visible block-trade tape to the
        agent so it can answer questions about flow.
      </Description>
      <Label htmlFor="ai-agent-url">Gateway URL</Label>
      <UrlInput
        id="ai-agent-url"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setSavedAt(null);
        }}
        placeholder={
          defaultUrl.length > 0 ? defaultUrl : 'wss://your-agent-host'
        }
        autoComplete="off"
        $invalid={!isValid}
      />
      {!isValid && (
        <ErrorText>Must be a ws:// or wss:// URL with a host.</ErrorText>
      )}
      <ButtonRow>
        {defaultUrl.length > 0 && trimmed !== defaultUrl && (
          <SecondaryButton
            type="button"
            onClick={() => {
              setDraft(defaultUrl);
              setSavedAt(null);
            }}
          >
            Use Default
          </SecondaryButton>
        )}
        <SaveButton type="submit" disabled={!isValid || !hasChanges}>
          {trimmed.length === 0 && (savedUrl ?? '').length > 0
            ? 'Clear'
            : 'Save'}
        </SaveButton>
        {savedAt != null && !hasChanges && <SavedText>Saved.</SavedText>}
      </ButtonRow>
    </Form>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Description = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
`;

const UrlInput = styled.input<{ $invalid?: boolean }>`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ $invalid }) =>
      $invalid === true
        ? 'var(--error-color, #f44336)'
        : 'var(--border-color)'};
  background: var(--bg-color-container);
  color: var(--text-primary);
  font-size: 12px;
  font-family: var(--font-mono, monospace);

  &:focus {
    outline: none;
    border-color: var(--accent-color, #4caf50);
  }
`;

const ErrorText = styled.div`
  font-size: 11px;
  color: var(--error-color, #f44336);
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SaveButton = styled.button`
  padding: 8px 20px;
  border-radius: 6px;
  border: none;
  background: var(--accent-color, #4caf50);
  color: var(--cta-text-color, #fff);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: transparent;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg, rgba(255, 255, 255, 0.08));
  }
`;

const SavedText = styled.span`
  font-size: 11px;
  color: var(--text-secondary);
`;

const Hint = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
`;
